class AnnouncementBarSlider extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector(".announcement-bar-slider-track");
    this.slides = this.querySelectorAll(".announcement-bar-slider-slide");
    this.controls = this.querySelectorAll(".announcement-bar-slider-control");

    this._currentIndex = 0;
    this._autoplayEvents = [];
    this._intervalId = null;

    this._resizeObserver = new ResizeObserver(() => this.update());
    this._onBlockSelect = this.onBlockSelect.bind(this);

    this.handleControls();
    this.handleKeyboard();
    this.handleTouch();
  }

  connectedCallback() {
    if (!this.track && !this.slides.length) return;

    this._resizeObserver.observe(this.track);
    document.addEventListener("shopify:block:select", this._onBlockSelect);

    this.initAutoplay();
  }

  disconnectedCallback() {
    this._resizeObserver.disconnect();
    document.removeEventListener("shopify:block:select", this._onBlockSelect);

    this.stopAutoplay?.();

    this._autoplayEvents.forEach(([event, handler]) => {
      this.removeEventListener(event, handler);
    });
    this._autoplayEvents = [];
  }

  prev() {
    this._currentIndex = (this._currentIndex - 1 + this.slides.length) % this.slides.length;
    this.update();
  }

  next() {
    this._currentIndex = (this._currentIndex + 1) % this.slides.length;
    this.update();
  }

  go(index) {
    this._currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.update();
  }

  update() {
    if (!this.track || this.slides.length <= 1) return;

    const slideWidth = this.track.clientWidth;
    const offset = slideWidth * this._currentIndex;

    this.track.style.transform = `translateX(-${offset}px)`;

    this.handleAccessibility();
  }

  handleControls() {
    this.controls.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.name === "previous") this.prev();
        if (btn.name === "next") this.next();
      });
    });
  }

  handleAccessibility() {
    this.slides.forEach((slide, index) => {
      if (index === this._currentIndex) {
        slide.removeAttribute("aria-hidden");
        slide.removeAttribute("tabindex");

        slide.querySelectorAll("a, button").forEach((el) => {
          el.removeAttribute("tabindex");
        });
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.setAttribute("tabindex", "-1");

        slide.querySelectorAll("a, button").forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
      }
    });
  }

  handleKeyboard() {
    this.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); this.prev(); }
      if (e.key === "ArrowRight") { e.preventDefault(); this.next(); }
      if (e.key === "Home") this.go(0);
      if (e.key === "End") this.go(this.slides.length - 1);
    });
  }

  handleTouch() {
    let startX = 0;

    this.addEventListener("touchstart", (e) => {
      if (e.touches.length > 1) return;
      startX = e.touches[0].clientX;
    });

    this.addEventListener("touchend", (e) => {
      if (!startX || e.changedTouches.length > 1) return;

      const endX = e.changedTouches[0].clientX;
      const swipeDistance = startX - endX;

      if (swipeDistance > 10) this.next();
      if (swipeDistance < -10) this.prev();

      this.startAutoplay?.();
    });
  }

  onBlockSelect(event) {
    if (!event.target.closest(`[data-section-id="${this.dataset.sectionId}"]`)) return;

    const blockIndex = event.target.dataset.index;
    if (blockIndex !== undefined) this.go(Number(blockIndex));

    this.stopAutoplay?.();
  }

  initAutoplay() {
    const autoplayInterval = this.dataset.autoplayInterval;
    if (!autoplayInterval || autoplayInterval === "0") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.startAutoplay = () => {
      clearInterval(this._intervalId);

      this._intervalId = setInterval(
        () => this.next(),
        Number(autoplayInterval) * 1000
      );

      this.dataset.autoplayStopped = "false";
      this.track.setAttribute("aria-live", "off");
    };

    this.stopAutoplay = () => {
      clearInterval(this._intervalId);
      this.dataset.autoplayStopped = "true";
      this.track.setAttribute("aria-live", "polite");
    };

    this.startAutoplay();

    this._autoplayEvents.forEach(([event, handler]) => {
      this.removeEventListener(event, handler);
    });

    this._autoplayEvents = [
      ["focusin", this.stopAutoplay],
      ["focusout", this.startAutoplay],
      ["mouseenter", this.stopAutoplay],
      ["mouseleave", this.startAutoplay],
      ["touchstart", this.stopAutoplay],
    ];

    this._autoplayEvents.forEach(([event, handler]) => {
      this.addEventListener(event, handler);
    });
  }

}

customElements.define( "announcement-bar-slider", AnnouncementBarSlider );
